import { Fragment, ReactNode, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { ArticleCodeBlock } from "../../component/ArticleCodeBlock";
import ArticleTerminal from "../../component/ArticleTerminal";
import { ARTICLE_BY_SLUG, ArticleContentBlock, loadArticle, SiteArticle } from "../../content/articles";

const ROOT_MAX_WIDTH = '72rem';

const ArticlePageWrapper = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
`;

const HeroCard = styled.article`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: clamp(1.25rem, 4vw, 2.5rem);
    max-width: ${ROOT_MAX_WIDTH};
    width: 100%;
    box-sizing: border-box;
`;

const ArticleChrome = styled.div`
    width: 100%;
`;

const MetaHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-bottom: 1.5rem;
    border-bottom: 2px solid ${({ theme }) => theme.colour.COLOUR_MID};
`;

const Intro = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 4rem;
    padding-top: 2rem;

    @media (max-width: 900px) {
        flex-direction: column;
        gap: 1.5rem;
        /* Stacked, flex-start would size each column to its own content and let
           the 40em cap below overflow a phone; stretch keeps them in the column. */
        align-items: stretch;
    }
`;

const IntroHeadingColumn = styled.div`
    flex: 1 1 20rem;
    min-width: 0;
    position: sticky;
    top: 6rem;

    @media (max-width: 900px) {
        flex-basis: auto;
        position: static;
    }
`;

const IntroContentColumn = styled.div`
    flex: 0 1 40em;
    min-width: 0;
    max-width: min(40em, 100%);
`;

const Header = styled.h5`
    margin: 0;
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-size: 0.8rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colour.LIGHT_GREY};
`;

const Title = styled.h1`
    margin: 0.35rem 0 1rem;
    font-family: ${({ theme }) => theme.font.FONT_SERIF_ROBOTO_SERIF};
    font-weight: 700;
    font-size: 1.5em;
    line-height: 1.1;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
`;

const Contents = styled.ol`
    list-style: none;
    margin: 1.5rem 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.45rem;

    @media (max-width: 900px) {
        display: none;
    }
`;

const ContentsLink = styled.a`
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-size: 0.85rem;
    line-height: 1.35;
    color: ${({ theme }) => theme.colour.LIGHT_GREY};
    text-decoration: none;
    transition: color 150ms ease;

    &:hover {
        color: ${({ theme }) => theme.colour.COLOUR_LIGHT};
    }
`;

const Description = styled.p`
    margin: 0 0 1.4rem;
    max-width: 68ch;
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-size: 1.08rem;
    line-height: 1.55;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
`;

const ContentStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.15rem;
`;

const ContentParagraph = styled.p`
    margin: 0;
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-size: 1rem;
    line-height: 1.7;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
`;

const ContentSubtitle = styled.h2`
    margin: 1rem 0 0;
    font-family: ${({ theme }) => theme.font.FONT_SERIF_ROBOTO_SERIF};
    font-weight: 700;
    font-size: 1.3rem;
    line-height: 1.3;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
    scroll-margin-top: 6rem;
`;

const ContentList = styled.ul`
    margin: 0;
    padding-left: 1.4rem;
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-size: 1rem;
    line-height: 1.6;
    color: ${({ theme }) => theme.colour.OFF_WHITE};

    li::marker {
        color: ${({ theme }) => theme.colour.COLOUR_LIGHT};
    }
`;

const ContentNote = styled.aside`
    margin: 0.25rem 0;
    padding: 0.85rem 1.1rem;
    border-left: 3px solid ${({ theme }) => theme.colour.COLOUR_LIGHT};
    background-color: rgba(255, 166, 0, 0.06);
    font-family: ${({ theme }) => theme.font.FONT_SANS_RED_HAT_DISPLAY};
    font-size: 0.95rem;
    line-height: 1.65;
    color: ${({ theme }) => theme.colour.OFF_WHITE};
`;

const InlineCode = styled.code`
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 0.86em;
    padding: 0.08em 0.35em;
    border-radius: 3px;
    background-color: rgba(96, 96, 104, 0.28);
    color: ${({ theme }) => theme.colour.OFF_WHITE};
`;

const InlineLink = styled.a`
    color: ${({ theme }) => theme.colour.COLOUR_LIGHT};
    text-decoration: none;
    border-bottom: 1px solid rgba(255, 166, 0, 0.45);
    transition: border-color 150ms ease;

    &:hover {
        border-bottom-color: ${({ theme }) => theme.colour.COLOUR_LIGHT};
    }
`;

const InternalLink = InlineLink.withComponent(Link);

const TagRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.9rem;
`;

const Tag = styled.img`
    height: 2.5rem;
    width: auto;
    object-fit: contain;
`;

const INLINE_PATTERN = /\[([^\]]+)\]\(([^)\s]+)\)|`([^`]+)`/g;

/* [label](url) becomes a link, internal when the url starts with a slash, and
   `code` becomes inline code. Everything else is passed through verbatim. */
export function renderInline(text: string): ReactNode {
    const parts: ReactNode[] = [];
    let cursor = 0;

    for (const match of text.matchAll(INLINE_PATTERN)) {
        const index = match.index ?? 0;
        if (index > cursor) {
            parts.push(text.slice(cursor, index));
        }

        const [, label, url, code] = match;
        if (code !== undefined) {
            parts.push(<InlineCode key={index}>{code}</InlineCode>);
        } else if (url.startsWith("/")) {
            parts.push(<InternalLink key={index} to={url}>{label}</InternalLink>);
        } else {
            parts.push(
                <InlineLink key={index} href={url} target="_blank" rel="noreferrer">
                    {label}
                </InlineLink>
            );
        }

        cursor = index + match[0].length;
    }

    if (cursor < text.length) {
        parts.push(text.slice(cursor));
    }

    return parts.map((part, index) => <Fragment key={index}>{part}</Fragment>);
}

const subtitleId = (text: string) =>
    text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

type ArticlePageLayoutProps = {
    article: SiteArticle;
};

function renderContentBlock(block: ArticleContentBlock, index: number) {
    switch (block.type) {
        case "paragraph":
            return <ContentParagraph key={index}>{renderInline(block.text)}</ContentParagraph>;
        case "subtitle":
            return <ContentSubtitle key={index} id={subtitleId(block.text)}>{block.text}</ContentSubtitle>;
        case "list":
            return (
                <ContentList key={index}>
                    {block.items.map((item, itemIndex) => (
                        <li key={itemIndex}>{renderInline(item)}</li>
                    ))}
                </ContentList>
            );
        case "note":
            return <ContentNote key={index}>{renderInline(block.text)}</ContentNote>;
        case "terminal":
            return <ArticleTerminal key={index} title={block.title} lines={block.lines} />;
        case "code":
            return <ArticleCodeBlock key={index} language={block.language} code={block.code} title={block.title} />;
    }
}

function resolveAssetPath(path: string): string {
    return path.startsWith("/") ? path : `/${path}`;
}

function ArticlePageLayout({ article }: ArticlePageLayoutProps) {
    const subtitles = article.content.filter(
        (block): block is Extract<ArticleContentBlock, { type: "subtitle" }> => block.type === "subtitle"
    );

    return (
        <ArticlePageWrapper>
            <HeroCard>
                <ArticleChrome>
                    <MetaHeader>
                        <Header>{article.header}</Header>
                        {article.tags.length > 0 && (
                            <TagRow>
                                {article.tags.map((tagPath) => (
                                    <Tag key={tagPath} src={resolveAssetPath(tagPath)} alt="" />
                                ))}
                            </TagRow>
                        )}
                    </MetaHeader>
                    <Intro>
                        <IntroHeadingColumn>
                            <Title>{article.title}</Title>
                            {subtitles.length > 1 && (
                                <Contents aria-label="Contents">
                                    {subtitles.map((block) => (
                                        <li key={block.text}>
                                            <ContentsLink href={`#${subtitleId(block.text)}`}>{block.text}</ContentsLink>
                                        </li>
                                    ))}
                                </Contents>
                            )}
                        </IntroHeadingColumn>
                        <IntroContentColumn>
                            <Description>{article.description}</Description>
                            <ContentStack>
                                {article.content.map((block, index) => renderContentBlock(block, index))}
                            </ContentStack>
                        </IntroContentColumn>
                    </Intro>
                </ArticleChrome>
            </HeroCard>
        </ArticlePageWrapper>
    );
}

export default function ArticleView() {
    const params = useParams<{ slug: string }>();
    const slug = params.slug ?? "";
    const known = slug in ARTICLE_BY_SLUG;
    const [article, setArticle] = useState<SiteArticle | null>(null);

    useEffect(() => {
        if (!known) return undefined;
        let current = true;
        setArticle(null);
        loadArticle(slug).then((loaded) => {
            if (current) setArticle(loaded);
        });
        return () => {
            current = false;
        };
    }, [slug, known]);

    if (!known) {
        return <Navigate to="/" />;
    }

    return article ? <ArticlePageLayout article={article} /> : null;
}
